import { action, computed, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../app/data/client';
import * as model from '../../app/data/model';
import { ShoppingCartItemData, ShoppingCartItemGroupData } from '../../app/data/model';
import {
  ConfigurationDetailsPageTranslation,
  ConfiguratorPageTranslation,
} from '../../app/localization/SiteTranslation';
import { AsyncCommand, getEmptyMoneyData } from '../../app/shared/common';
import { IEventAggregator } from '../../app/shared/EventAggregator';
import { NumberInput } from '../../app/shared/Form';
import Logger from '../../app/shared/Logger';
import { INavigationService } from '../../app/shared/NavigationService';
import { StoreState } from '../../app/StoreState';
import { ShoppingCartUpdatedEvent } from '../cart/events/ShoppingCartUpdatedEvent';
import { AddedToCartModalState } from '../configurator/AddedToCartModal/AddedToCartModalState';
import { ConfigurationActionsTranslation } from '../localization/SiteTranslation';

export class ConfigurationDetailsActionsState {
  @observable.ref
  public recentlyAddedItemModal: AddedToCartModalState<ShoppingCartItemData | ShoppingCartItemGroupData>;

  public addToCartCommand: AsyncCommand;
  public loadPriceCommand: AsyncCommand;
  public validateConfigurationCommand: AsyncCommand;

  @observable.ref public price: model.MoneyData;

  @observable quantityInput: NumberInput;
  quantityMin: number;
  @observable
  quantityMax: number;

  @observable codeIsValid: Boolean;
  @observable unableToValidateCode: Boolean;

  constructor(
    public readonly details: model.GetProductConfigurationByCodeQueryResponse,
    public readonly code: string,
    public readonly store: StoreState,
    private readonly client: IApiClient,
    private readonly navigation: INavigationService,
    private readonly eventAggregator: IEventAggregator,
    public readonly configurationActionsTranslation: ConfigurationActionsTranslation,
    public readonly configuratorPageTranslation: ConfiguratorPageTranslation,
    public readonly translations: ConfigurationDetailsPageTranslation,
  ) {
    makeObservable(this);

    this.recentlyAddedItemModal = new AddedToCartModalState(
      this.navigation,
      this.configurationActionsTranslation.addedToCartModal,
    );

    this.validateConfigurationCommand = new AsyncCommand(this.validateConfigurationCode);
    this.loadPriceCommand = new AsyncCommand(this.loadProductPrice);
    this.loadPriceCommand.invoke();
    this.addToCartCommand = new AsyncCommand(this.addProductToDealerCart);

    this.quantityMin = 1;
    this.quantityMax = 50;
    this.quantityInput = new NumberInput(1);
  }

  private validateConfigurationCode = async () => {
    if (!this.code) {
      this.setCodeValidationResult(false, false);
    }

    try {
      const codeValidationResult =
        this.details.configuration.origin === model.ProductConfigurationOrigin.RawConfigurator
          ? await this.client.send(
              new model.ValidateProductConfigurationCodeQuery({
                code: this.code,
                shoppingContext: this.store.shoppingContext,
              }),
            )
          : false;
      this.setCodeValidationResult(codeValidationResult, false);
    } catch (error) {
      Logger.exception(`An error occurred while validating configuration code ${this.code}`, error);
      this.setCodeValidationResult(false, true);
    }
  };

  private loadProductPrice = async () => {
    await this.validateConfigurationCommand.invoke();

    if (this.codeIsValid === false) {
      this.setPrice(getEmptyMoneyData());
      return;
    }

    try {
      const priceResponse = await this.client.send(
        new model.GetProductConfigurationPriceQuery({
          code: this.code,
          shoppingContext: this.store.shoppingContext,
        }),
      );
      this.setPrice(priceResponse?.productPrice);
    } catch (error) {
      Logger.exception(`An error occurred while loading prices for code ${this.code}`, error);
      this.setPrice(getEmptyMoneyData());
    }
  };

  private addProductToDealerCart = async () => {
    const command = new model.AddProductFromRawConfigurationToDealerCartCommand({
      shoppingContext: this.store.shoppingContext,
      rawConfigurationCode: this.code,
      quantity: this.quantityInput.value,
    });

    const response = await this.client.send(command);

    this.openRecentItemModal(response.recentItem, response.quantityAdded);
    this.eventAggregator.publish(new ShoppingCartUpdatedEvent(response.dealerCarts.cartsSummary));
  };

  @action.bound
  private setCodeValidationResult = (isValid: Boolean, unableToValidate: Boolean) => {
    this.codeIsValid = isValid;
    this.unableToValidateCode = unableToValidate;
  };

  @action.bound
  private setPrice = (price: model.MoneyData) => {
    this.price = price;
  };

  @action.bound
  updateQuantity(value: number) {
    this.quantityInput.onChange(value);
  }

  @computed get total() {
    const total = { ...this.price };
    total.amount = this.quantityInput.value ? this.quantityInput.value * total.amount : total.amount;
    return total;
  }

  @action.bound
  private openRecentItemModal(lastItem: model.ShoppingCartItemData, lastItemAddedQuantity: number) {
    this.recentlyAddedItemModal.setRecentlyAddedItem(lastItem, lastItemAddedQuantity);
    this.recentlyAddedItemModal.open();
  }
}
